/** PMS-S547's broker carrier summary, including only broker-visible fields. */
export interface BrokerCarrierSummary {
  id: string;
  displayName: string;
  legalName: string | null;
  naicCode: string | null;
  websiteUrl: string | null;
  logoUrl: string | null;
  addresses: {
    kind: "mailing" | "claims" | "premium_remittance";
    attention: string | null;
    street: string;
    addressLine2: string | null;
    city: string;
    state: string;
    zip: string;
  }[];
  financialStrength: {
    agency: "am_best";
    rating: string;
    ratingLabel: string;
    financialSizeCategory: string | null;
    outlook: string | null;
    underReview: boolean;
    effectiveDate: string;
  } | null;
  market: {
    state: string;
    lineOfBusiness:
      | "general_liability"
      | "workers_compensation"
      | "commercial_auto"
      | null;
    type: "admitted" | "surplus_lines";
  } | null;
}

/** Unknown/malformed profiles never invent a rating or market type. */
const record = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);
const nullableText = (v: unknown) => v === null || typeof v === "string";
function isRating(
  v: unknown,
): v is NonNullable<BrokerCarrierSummary["financialStrength"]> {
  return (
    record(v) &&
    v.agency === "am_best" &&
    typeof v.rating === "string" &&
    typeof v.ratingLabel === "string" &&
    typeof v.effectiveDate === "string" &&
    typeof v.underReview === "boolean" &&
    nullableText(v.financialSizeCategory) &&
    nullableText(v.outlook)
  );
}
function isMarket(
  v: unknown,
): v is NonNullable<BrokerCarrierSummary["market"]> {
  return (
    record(v) &&
    typeof v.state === "string" &&
    (v.type === "admitted" || v.type === "surplus_lines") &&
    (v.lineOfBusiness === null ||
      v.lineOfBusiness === "general_liability" ||
      v.lineOfBusiness === "workers_compensation" ||
      v.lineOfBusiness === "commercial_auto")
  );
}
function isAddress(v: unknown): v is BrokerCarrierSummary["addresses"][number] {
  return (
    record(v) &&
    (v.kind === "mailing" ||
      v.kind === "claims" ||
      v.kind === "premium_remittance") &&
    nullableText(v.attention) &&
    nullableText(v.addressLine2) &&
    [v.street, v.city, v.state, v.zip].every((part) => typeof part === "string")
  );
}
function isProfile(v: unknown): v is BrokerCarrierSummary {
  return (
    record(v) &&
    typeof v.id === "string" &&
    typeof v.displayName === "string" &&
    [v.legalName, v.naicCode, v.websiteUrl, v.logoUrl].every(nullableText) &&
    Array.isArray(v.addresses) &&
    v.addresses.every(isAddress) &&
    (v.financialStrength === null || isRating(v.financialStrength)) &&
    (v.market === null || isMarket(v.market))
  );
}
export function readCarrierProfile(
  value: unknown,
): BrokerCarrierSummary | null | undefined {
  return value === null ? null : isProfile(value) ? value : undefined;
}
